'use client';

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/** Radix `Select` treats '' as "no value", so "nothing chosen" needs its own sentinel. */
export const UNASSIGNED = '__unassigned__';
/** Placeholder for a dropdown with nothing to offer — always rendered disabled. */
const NO_OPTIONS = '__none__';

export interface CrewOption { id: string; label: string }

/** `null` for "nothing chosen", which is what every API payload wants. */
export function crewValueToId(value: string): string | null {
  return value === UNASSIGNED ? null : value;
}

/** One rail dropdown. Sentinel-backed so "Unassigned" is a real, selectable state. */
export function RailSelect({ id, label, value, options, emptyHint, disabled, onChange }: {
  id: string;
  label: string;
  value: string;
  options: CrewOption[];
  emptyHint: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const hintId = `${id}-hint`;
  const showHint = options.length === 0;
  return (
    <div className="space-y-1.5">
      <Label id={labelId} htmlFor={id} className="gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {label} <span className="lowercase tracking-normal opacity-70">optional</span>
      </Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          id={id}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-describedby={showHint ? hintId : undefined}
          className="w-full bg-background border-border text-foreground"
        >
          <SelectValue id={valueId} />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border">
          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
          {showHint
            ? <SelectItem value={NO_OPTIONS} disabled>{emptyHint}</SelectItem>
            : options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {/* On the closed field, not only inside the menu — otherwise the operator has to open the
          dropdown to discover there is nothing in it. */}
      {showHint && <p id={hintId} className="text-[11px] text-muted-foreground">{emptyHint}</p>}
    </div>
  );
}

